/** /api/school/dingtalk(园长的钉钉对接)+ /api/school/parent(家长只读视图)。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authHeaders } from "./school";

async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method,
    headers: { ...authHeaders(), ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const b = (await r.json().catch(() => ({}))) as { detail?: unknown };
    throw new Error(typeof b.detail === "string" ? b.detail : `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

export interface DtMember {
  userid: string;
  name: string;
  has_mobile: boolean;
  email: string | null;
  is_teacher: boolean;
  has_unionid: boolean;
  teacher_id: string | null;
  teacher_name: string | null;
  bound_by: "auto_mobile" | "auto_email" | "manual" | null;
  suggested_teacher_id: string | null;
}
export interface DtTeacher {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  has_account: boolean;
  dingtalk_userid: string | null;
}
export interface DtStudent {
  userid: string;
  name: string;
  class_name: string | null;
  student_id: string | null;
  student_name: string | null;
  student_grade: string | null;
  matched_by: "auto_name" | "manual" | null;
  guardians: string | null;
}
export interface DtStatus {
  school_id: string;
  configured: boolean;
  last_synced_at: string | null;
  guardian_count: number;
  members: DtMember[];
  teachers: DtTeacher[];
  students: DtStudent[];
}
export interface DtSyncResult {
  members: number;
  students: number;
  students_matched: number;
  guardians: number;
  relations: number;
  teachers_auto_bound: number;
}

const KEY = ["school", "dingtalk", "status"];

export function useDingtalkStatus() {
  return useQuery({ queryKey: KEY, queryFn: () => req<DtStatus>("GET", "/api/school/dingtalk/status") });
}

function useDtMutation<A, R>(fn: (a: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: KEY }) });
}

export const useDingtalkSync = () =>
  useDtMutation((_: void) => req<DtSyncResult>("POST", "/api/school/dingtalk/sync"));
export const useDingtalkBind = () =>
  useDtMutation((a: { teacher_id: string; dingtalk_userid: string }) => req("POST", "/api/school/dingtalk/bind", a));
export const useDingtalkUnbind = () =>
  useDtMutation((a: { teacher_id: string }) => req("POST", "/api/school/dingtalk/unbind", a));
export const useTeacherPhone = () =>
  useDtMutation((a: { teacher_id: string; phone: string | null }) =>
    req("PUT", `/api/school/dingtalk/teachers/${a.teacher_id}/phone`, { phone: a.phone }),
  );
export const useDingtalkStudentMatch = () =>
  useDtMutation((a: { dingtalk_userid: string; student_id: string | null }) =>
    req("POST", "/api/school/dingtalk/students/match", a),
  );

export interface ParentChild {
  id: string;
  name: string;
  name_en: string | null;
  grade: string;
  relation: string | null;
  classes: string[];
  homework: {
    title: string;
    class_name: string;
    assigned_date: string | null;
    due_date: string | null;
    score: number | null;
    max_score: number | null;
    teacher_note: string | null;
  }[];
  exams: { title: string; exam_date: string | null; total: number | null; max_total: number | null }[];
  feedback?: { period_start: string; period_end: string; content: string; teacher_name: string | null; published_at: string | null }[];
  conduct?: { total: number; base_score: number; records: { recorded_on: string; category: string; item: string | null; points: number; note: string | null }[] };
  semester_grades?: {
    courses: { name: string; semester: string; credits: number | null; final_score: number | null; total_score: number; gpa_label: string | null;
      gpa_points: number | null; domestic_label: string | null; comment: string | null;
      items: { label: string; weight: number; average: number | null; entries: { label: string; date: string | null; score: number; max: number }[] }[] }[];
    gpa: number | null; gpa_adjusted: number | null; gpa_pending: boolean;
  };
  attendance?: { days: number; lessons: number; counts: Record<string, number>; absences: { date: string; period: string; status: string }[] };
}
export interface ParentMe {
  is_guardian: boolean;
  children: ParentChild[];
  unmatched_children?: number;
}

export function useParentMe() {
  return useQuery({ queryKey: ["school", "parent", "me"], queryFn: () => req<ParentMe>("GET", "/api/school/parent/me") });
}
